<template>
    <div class="tw:mt-6 tw:space-y-6">
        <BaseTable
            :columns="columns"
            :data="bases.data"
            :skeleton="loading"
            :skeleton-rows="5"
            empty-text="No hay deportistas en esta categoría"
            :default-col-width="defaultColWidth"
        >
            <!-- Skeleton headers -->
            <template #skeleton-header-nombre>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-14"></div>
            </template>
            <template #skeleton-header-apellidos>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-20"></div>
            </template>
            <template #skeleton-header-dni>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-14"></div>
            </template>
            <template #skeleton-header-email>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-24"></div>
            </template>
            <template #skeleton-header-opciones>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-28 tw:mx-auto"></div>
            </template>

            <!-- Skeleton rows -->
            <template #skeleton-nombre>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-20"></div>
            </template>
            <template #skeleton-apellidos>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-28"></div>
            </template>
            <template #skeleton-dni>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-20"></div>
            </template>
            <template #skeleton-email>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-44"></div>
            </template>
            <template #skeleton-opciones>
                <div class="tw:h-9 tw:w-28 tw:bg-gray-200 tw:rounded tw:mx-auto"></div>
            </template>

            <template #cell-nombre="{ row }">
                <span class="tw:text-sm tw:text-gray-700">{{ row.nombre }}</span>
            </template>

            <template #cell-apellidos="{ row }">
                <span class="tw:text-sm tw:text-gray-700">{{ row.apellidos }}</span>
            </template>

            <template #cell-dni="{ row }">
                <span class="tw:text-sm tw:text-gray-700">{{ row.dni }}</span>
            </template>

            <template #cell-email="{ row }">
                <span class="tw:text-sm tw:text-gray-700">{{ row.email }}</span>
            </template>

            <template #cell-opciones="{ row, index }">
                <OpcionesDropdownTableComponent
                    :last-index="bases.data.length > 3 && index >= bases.data.length - 3"
                    :options="getRowOptions(row)"
                />
            </template>
        </BaseTable>

        <TableFooter
            :value="filters"
            :meta="meta"
            @input="onPaginationUpdate"
        />

        <!-- Delete modals -->
        <BorrarModalComponent
            v-for="item in bases.data"
            :key="'resumen-borrar-' + item.id"
            :name="'resumen-borrar-' + item.id"
            :title="'Borrar deportista ' + item.nombre"
            @confirm="handleDelete(item.id)"
        >
            <p class="tw:text-base">¿Está seguro de que desea eliminar a <strong>{{ item.nombre }} {{ item.apellidos }}</strong>?</p>
        </BorrarModalComponent>
    </div>
</template>

<script setup>
    import { watch, getCurrentInstance } from 'vue'
    import { useResumenBases } from '@/composables/admin/bases/useResumenBases'
    import BaseTable from '@/modules/admin/ui/table/BaseTable.vue'
    import TableFooter from '@/modules/admin/ui/table/TableFooter.vue'
    import OpcionesDropdownTableComponent from '@/modules/admin/ui/OpcionesDropdownTableComponent.vue'
    import BorrarModalComponent from '@/modules/admin/ui/BorrarModalComponent.vue'

    const props = defineProps({
        search: { type: String, default: '' },
        defaultColWidth: {
            type: String,
            default: '130px'
        }
    })

    const { proxy } = getCurrentInstance()

    const { bases, loading, filters, meta, listaId, fetch, borrar } = useResumenBases()

    watch(() => props.search, val => {
        filters.value.search = val
        filters.value.page = 1
    })

    function onPaginationUpdate(val) {
        filters.value = val
    }

    const columns = [
        { key: 'nombre', label: 'Nombre' },
        { key: 'apellidos', label: 'Apellidos', class: 'tw:w-44' },
        { key: 'dni', label: 'DNI' },
        { key: 'email', label: 'Email', class: 'tw:w-64' },
        { key: 'opciones', label: 'Opciones', centered: true }
    ]

    function getRowOptions(row) {
        return [
            {
                to: {
                    name: 'back.bases.show',
                    params: { lista_id: listaId.value, base_id: row.id }
                },
                label: 'Ver',
                icon: 'eye'
            },
            {
                type: 'action',
                action: () => showModal('resumen-borrar-' + row.id),
                label: 'Borrar',
                icon: 'trash'
            }
        ]
    }

    function showModal(name) {
        proxy?.$modal?.show(name)
    }

    async function handleDelete(id) {
        await borrar(id)
        fetch()
    }
</script>
